<template>
  <div class="bim-categories">
        <div class="bim-categories__header">
          <div class="header-controls">
            <label class="header-label">Категория:</label>
            <select
              v-model="selectedCategoryId"
              @change="onCategoryChange"
              class="simple-select"
              :disabled="isLoadingCategories"
            >
              <option :value="null">Выберите категорию</option>
              <option
                v-for="category in categories"
                :key="category.bim_category_id"
                :value="category.bim_category_id"
              >
                {{ category.category_name }}
              </option>
            </select>
            
            <CustomButton
              class="import-btn"
              :loading="isImporting"
              @click="$refs.fileInput.click()"
            >
              <span>Импорт</span>
              <q-icon name="upload_file" class="import-btn__icon" size="14px" />
            </CustomButton>
            
            <q-btn
              icon="help_outline"
              flat
              dense
              round
              color="primary"
              @click="helpDialog = true"
              class="help-btn"
            >
              <q-tooltip>Помощь</q-tooltip>
            </q-btn>
            
            <input
              ref="fileInput"
              type="file"
              accept=".json"
              style="display: none"
              @change="handleFileSelect"
            />
          </div>
        </div>

    <div class="bim-categories__content">
      <!-- Колонка с типами -->
      <div class="bim-categories__column">
        <div class="column-header">
          <q-checkbox
            v-if="types.length"
            :model-value="allTypesSelected"
            @update:model-value="toggleAllTypes"
            size="xs"
            dense
            class="header-checkbox"
          />
          <q-icon name="construction" size="14px" />
          <span class="column-title">Типы конструкций</span>
          <q-badge v-if="types.length" :label="filteredTypes.length + '/' + types.length" color="grey-4" text-color="grey-8" />
          <q-toggle
            v-model="isGroupMode"
            dense
            size="xs"
            color="primary"
            @update:model-value="onGroupModeChange"
            style="margin-left: 8px;"
          >
            <q-tooltip>Режим групповых действий</q-tooltip>
          </q-toggle>
          <q-btn
            v-if="selectedTypes.length > 0 && !isGroupMode"
            icon="delete"
            flat
            dense
            round
            size="xs"
            color="negative"
            @click="confirmDeleteSelectedTypes"
          >
            <q-tooltip>Удалить выбранные ({{ selectedTypes.length }})</q-tooltip>
          </q-btn>
          <q-btn
            v-if="selectedCategory"
            icon="add"
            flat
            dense
            round
            size="xs"
            color="primary"
            @click="openTypeDialog()"
          >
            <q-tooltip>Добавить</q-tooltip>
          </q-btn>
        </div>
        <div v-if="selectedCategory && types.length" class="search-container">
          <q-input
            v-model="typeSearchQuery"
            dense
            outlined
            placeholder="Поиск..."
            clearable
            class="search-input"
          >
            <template v-slot:prepend>
              <q-icon name="search" size="16px" />
            </template>
          </q-input>
          
          <select
            v-model="typeFilterMode"
            class="simple-select filter-select"
          >
            <option value="all">Все</option>
            <option value="filled">Только заполненные</option>
            <option value="unfilled">Только незаполненные</option>
          </select>
        </div>
        <div v-if="!selectedCategory" class="empty-state">
          <q-icon name="arrow_upward" color="grey-5" />
          <p>← Выберите категорию</p>
        </div>
        <q-list v-else-if="!isLoadingTypes && filteredTypes.length > 0" dense class="column-list">
          <q-item
            v-for="type in filteredTypes"
            :key="type.bim_type_id"
            :clickable="!isGroupMode"
            v-ripple="!isGroupMode"
            :active="!isGroupMode && selectedType?.bim_type_id === type.bim_type_id"
            @click="!isGroupMode && selectType(type)"
            class="list-item"
            dense
          >
            <q-item-section side style="min-width: 20px; padding-right: 4px;">
              <q-checkbox
                :model-value="selectedTypes.includes(type.bim_type_id)"
                @update:model-value="(val) => toggleTypeSelection(type.bim_type_id, val)"
                @click.stop
                size="xs"
                dense
              />
            </q-item-section>
            <q-item-section>
              <q-item-label class="item-label">{{ type.type_name }}</q-item-label>
              <q-item-label 
                v-if="type.bi_name" 
                caption 
                class="item-caption" 
                style="font-size: 12px; color: #555; font-weight: 500; margin-top: 3px; padding-left: 8px; border-left: 2px solid #bdbdbd;"
              >
                {{ type.bi_name }}
              </q-item-label>
              <q-item-label caption class="item-caption">ID: {{ type.bim_type_id }}</q-item-label>
            </q-item-section>
            <q-item-section side class="type-info-section">
              <div class="type-line-block" :class="{ 'line-empty': !type.floor_param_type_name }">
                <div class="type-info-item line-header" :style="{ color: type.floor_param_type_name ? '#2196F3' : '#f44336' }">
                  <span class="type-info-label">Линия:</span>
                  <span class="type-info-value" :style="{ fontWeight: '500' }">
                    {{ type.floor_param_type_name || 'Не заполнено ⚠️' }}
                  </span>
                </div>
                <div v-if="type.floor_param_type_name" class="type-info-details">
                  <div class="type-info-item">
                    <span class="type-info-label-small">Замер:</span>
                    <span class="type-info-value-small">{{ type.param_name || '-' }}</span>
                  </div>
                  <div class="type-info-item">
                    <span class="type-info-label-small">Ед. изм:</span>
                    <span class="type-info-value-small">{{ type.unit_name || '-' }}</span>
                  </div>
                </div>
              </div>
              
              <div v-if="type.floor_param_type_name2" class="type-line-block">
                <div class="type-info-item line-header" style="color: #2196F3;">
                  <span class="type-info-label">Линия 2:</span>
                  <span class="type-info-value" style="font-weight: 500;">
                    {{ type.floor_param_type_name2 }}
                  </span>
                </div>
                <div class="type-info-details">
                  <div class="type-info-item">
                    <span class="type-info-label-small">Замер:</span>
                    <span class="type-info-value-small">{{ type.param_name2 || '-' }}</span>
                  </div>
                  <div class="type-info-item">
                    <span class="type-info-label-small">Ед. изм:</span>
                    <span class="type-info-value-small">{{ type.unit_name2 || '-' }}</span>
                  </div>
                </div>
              </div>
              
              <div v-if="type.floor_param_type_name3" class="type-line-block">
                <div class="type-info-item line-header" style="color: #2196F3;">
                  <span class="type-info-label">Линия 3:</span>
                  <span class="type-info-value" style="font-weight: 500;">
                    {{ type.floor_param_type_name3 }}
                  </span>
                </div>
                <div class="type-info-details">
                  <div class="type-info-item">
                    <span class="type-info-label-small">Замер:</span>
                    <span class="type-info-value-small">{{ type.param_name3 || '-' }}</span>
                  </div>
                  <div class="type-info-item">
                    <span class="type-info-label-small">Ед. изм:</span>
                    <span class="type-info-value-small">{{ type.unit_name3 || '-' }}</span>
                  </div>
                </div>
              </div>
            </q-item-section>
            <q-item-section side class="action-buttons" v-if="!isGroupMode">
              <div class="action-icons">
                <q-btn
                  icon="edit"
                  flat
                  dense
                  round
                  size="xs"
                  color="primary"
                  @click.stop="openTypeDialog(type)"
                >
                  <q-tooltip>Редактировать</q-tooltip>
                </q-btn>
              </div>
            </q-item-section>
          </q-item>
        </q-list>
        <div v-else-if="!isLoadingTypes && filteredTypes.length === 0" class="empty-state">
          <q-icon name="search_off" color="grey-5" />
          <p>{{ typeSearchQuery ? 'Ничего не найдено' : 'Нет типов конструкций' }}</p>
        </div>
        <div v-else class="loading-container">
          <q-spinner color="primary" size="30px" />
        </div>
      </div>

          <!-- Колонка с параметрами (обычный режим) -->
          <div class="bim-categories__column" v-if="!isGroupMode">
            <div class="column-header">
              <q-checkbox
                v-if="params.length"
                :model-value="allParamsSelected"
                @update:model-value="toggleAllParams"
                size="xs"
                dense
                class="header-checkbox"
              />
              <q-icon name="settings" size="14px" />
              <span class="column-title">Параметры</span>
              <q-badge v-if="params.length" :label="filteredParams.length + '/' + params.length" color="grey-4" text-color="grey-8" />
              <q-toggle
                :model-value="paramFilterMode === 'active'"
                @update:model-value="(val) => onParamFilterChange(val ? 'active' : 'all')"
                dense
                size="xs"
                color="positive"
                style="margin-left: 8px;"
              >
                <q-tooltip>{{ paramFilterMode === 'active' ? 'Показать все' : 'Только активные' }}</q-tooltip>
              </q-toggle>
              <q-btn
                v-if="selectedParams.length > 0"
                icon="delete"
                flat
                dense
                round
                size="xs"
                color="negative"
                @click="confirmDeleteSelectedParams"
              >
                <q-tooltip>Удалить выбранные ({{ selectedParams.length }})</q-tooltip>
              </q-btn>
              <q-btn
                icon="add"
                flat
                dense
                round
                size="xs"
                color="primary"
                @click="openParamDialog()"
              >
                <q-tooltip>Добавить</q-tooltip>
              </q-btn>
            </div>
            <div v-if="selectedType && params.length" class="search-container">
              <q-input
                v-model="paramSearchQuery"
                dense
                outlined
                placeholder="Поиск..."
                clearable
                class="search-input"
              >
                <template v-slot:prepend>
                  <q-icon name="search" size="16px" />
                </template>
              </q-input>
            </div>
        <div v-if="!selectedType" class="empty-state">
          <q-icon name="arrow_back" color="grey-5" />
          <p>← Выберите тип</p>
        </div>
        <q-list v-else-if="!isLoadingParams && filteredParams.length > 0" dense class="column-list">
          <q-item
            v-for="param in filteredParams"
            :key="param.bim_param_id"
            class="list-item"
            dense
          >
            <q-item-section side style="min-width: 20px; padding-right: 4px;">
              <q-checkbox
                :model-value="selectedParams.includes(param.bim_param_id)"
                @update:model-value="(val) => toggleParamSelection(param.bim_param_id, val)"
                @click.stop
                size="xs"
                dense
              />
            </q-item-section>
            <q-item-section side style="min-width: 40px; padding-right: 8px;">
              <q-toggle
                :model-value="param.is_selected"
                @update:model-value="(val) => toggleParamActive(param, val)"
                color="positive"
                size="sm"
              />
            </q-item-section>
            <q-item-section>
              <q-item-label class="item-label">{{ param.param_name }}</q-item-label>
              <q-item-label caption class="item-caption">
                ID: {{ param.bim_param_id }}
              </q-item-label>
              <q-item-label caption class="item-caption" v-if="param.buffer_property_name">
                Свойство: {{ param.buffer_property_name }}
              </q-item-label>
            </q-item-section>
          </q-item>
        </q-list>
        <div v-else-if="!isLoadingParams && filteredParams.length === 0" class="empty-state">
          <q-icon name="search_off" color="grey-5" />
          <p>{{ paramSearchQuery ? 'Ничего не найдено' : 'Нет параметров' }}</p>
        </div>
        <div v-else class="loading-container">
          <q-spinner color="primary" size="30px" />
        </div>
      </div>

          <!-- Колонка с параметрами (групповой режим) -->
          <div class="bim-categories__column" v-else>
            <div class="column-header">
              <q-checkbox
                v-if="groupParams.length"
                :model-value="allGroupParamsSelected"
                @update:model-value="toggleAllGroupParams"
                size="xs"
                dense
                class="header-checkbox"
              />
              <q-icon name="settings" size="14px" />
              <span class="column-title">Общие параметры</span>
              <q-badge v-if="groupParams.length" :label="filteredGroupParams.length + '/' + groupParams.length" color="grey-4" text-color="grey-8" />
              <q-btn
                v-if="selectedParams.length > 0"
                icon="check_circle"
                flat
                dense
                round
                size="xs"
                color="positive"
                @click="activateSelectedGroupParams(true)"
              >
                <q-tooltip>Активировать выбранные</q-tooltip>
              </q-btn>
              <q-btn
                v-if="selectedParams.length > 0"
                icon="cancel"
                flat
                dense
                round
                size="xs"
                color="warning"
                @click="activateSelectedGroupParams(false)"
              >
                <q-tooltip>Деактивировать выбранные</q-tooltip>
              </q-btn>
              <q-btn
                v-if="selectedParams.length > 0"
                icon="delete"
                flat
                dense
                round
                size="xs"
                color="negative"
                @click="confirmDeleteSelectedGroupParams"
              >
                <q-tooltip>Удалить выбранные у всех типов ({{ selectedParams.length }})</q-tooltip>
              </q-btn>
            </div>
            <div v-if="selectedTypes.length >= 2 && groupParams.length" class="search-container">
              <q-input
                v-model="groupParamSearchQuery"
                dense
                outlined
                placeholder="Поиск..."
                clearable
                class="search-input"
              >
                <template v-slot:prepend>
                  <q-icon name="search" size="16px" />
                </template>
              </q-input>
            </div>
            <div v-if="selectedTypes.length < 2" class="empty-state">
              <q-icon name="arrow_back" color="grey-5" />
              <p>← Выберите несколько типов</p>
            </div>
            <q-list v-else-if="!isLoadingGroupParams && filteredGroupParams.length > 0" dense class="column-list">
          <q-item
            v-for="param in filteredGroupParams"
            :key="param.bim_param_id"
            class="list-item"
            dense
          >
            <q-item-section side style="min-width: 20px; padding-right: 4px;">
              <q-checkbox
                :model-value="selectedParams.includes(param.bim_param_id)"
                @update:model-value="(val) => toggleParamSelection(param.bim_param_id, val)"
                @click.stop
                size="xs"
                dense
              />
            </q-item-section>
            <q-item-section>
              <q-item-label class="item-label">{{ param.param_name }}</q-item-label>
              <q-item-label caption class="item-caption">
                ID: {{ param.bim_param_id }}
              </q-item-label>
              <q-item-label caption class="item-caption" v-if="param.buffer_property_name">
                Свойство: {{ param.buffer_property_name }}
              </q-item-label>
            </q-item-section>
          </q-item>
        </q-list>
        <div v-else-if="!isLoadingGroupParams && filteredGroupParams.length === 0 && groupParams.length === 0" class="empty-state">
          <q-icon name="search_off" color="grey-5" />
          <p>Нет общих параметров</p>
        </div>
        <div v-else-if="!isLoadingGroupParams && filteredGroupParams.length === 0 && groupParams.length > 0" class="empty-state">
          <q-icon name="search_off" color="grey-5" />
          <p>Ничего не найдено</p>
        </div>
        <div v-else class="loading-container">
          <q-spinner color="primary" size="30px" />
        </div>
      </div>
    </div>

    <!-- Диалог для добавления/редактирования типа -->
    <q-dialog v-model="typeDialog" persistent>
      <q-card class="bim-modal">
        <q-card-section class="bim-modal__header">
          <div class="bim-modal__title">{{ editingType ? 'Редактировать тип конструкции' : 'Добавить тип конструкции' }}</div>
          <q-btn icon="close" flat round dense v-close-popup class="bim-modal__close" />
        </q-card-section>

        <q-card-section class="bim-modal__content">
          <q-form @submit="saveType" class="bim-form">
            <div class="bim-form__section">
              <div class="bim-form__item">
                <CustomInput
                  label="Название типа"
                  type="text"
                  :required="true"
                  :value="typeForm.type_name"
                  @change_input="(value) => typeForm.type_name = value"
                  autofocus
                />
              </div>
              <div class="bim-form__item">
                <CustomSelect
                  label="Линия"
                  :options="floorParamTypes"
                  option-label-key="floor_param_type_name"
                  option-value-key="floor_param_type_id"
                  :value="typeForm.floor_param_type_id"
                  @change="(value) => typeForm.floor_param_type_id = value"
                  :loading="isLoadingFloorParamTypes"
                  placeholder="Выберите линию"
                  :clearable="true"
                />
              </div>
              <div class="bim-form__item">
                <CustomSelect
                  label="Линия 2"
                  :options="floorParamTypes"
                  option-label-key="floor_param_type_name"
                  option-value-key="floor_param_type_id"
                  :value="typeForm.floor_param_type_id2"
                  @change="(value) => typeForm.floor_param_type_id2 = value"
                  :loading="isLoadingFloorParamTypes"
                  placeholder="Выберите линию 2"
                  :clearable="true"
                />
              </div>
              <div class="bim-form__item">
                <CustomSelect
                  label="Линия 3"
                  :options="floorParamTypes"
                  option-label-key="floor_param_type_name"
                  option-value-key="floor_param_type_id"
                  :value="typeForm.floor_param_type_id3"
                  @change="(value) => typeForm.floor_param_type_id3 = value"
                  :loading="isLoadingFloorParamTypes"
                  placeholder="Выберите линию 3"
                  :clearable="true"
                />
              </div>
            </div>

            <div class="bim-modal__footer">
              <CustomButton
                @click="typeDialog = false"
                :disabled="isSaving"
                outlined
              >
                Отмена
              </CustomButton>
              <CustomButton
                type="submit"
                :loading="isSaving"
              >
                {{ editingType ? 'Сохранить' : 'Создать' }}
              </CustomButton>
            </div>
          </q-form>
        </q-card-section>
      </q-card>
    </q-dialog>

    <!-- Диалог для добавления параметра к типу -->
    <q-dialog v-model="paramDialog" persistent>
      <q-card class="bim-modal">
        <q-card-section class="bim-modal__header">
          <div class="bim-modal__title">{{ editingParam ? 'Изменить параметр' : 'Добавить параметр' }}</div>
          <q-btn icon="close" flat round dense v-close-popup class="bim-modal__close" />
        </q-card-section>

        <q-card-section class="bim-modal__content">
          <q-form @submit.prevent="saveParam" class="bim-form">
            <div class="bim-form__section">
              <div class="bim-form__item">
                <CustomSelect
                  label="Параметр"
                  :options="formattedParamOptions"
                  option-label-key="param_name"
                  option-value-key="bim_param_id"
                  :value="paramForm.bim_param_id"
                  @change="(value) => paramForm.bim_param_id = value"
                  :loading="isLoadingAllParams"
                  :required="true"
                  placeholder="Выберите параметр"
                  :clearable="!editingParam"
                />
              </div>
            </div>

            <div class="bim-modal__footer">
              <CustomButton
                @click="paramDialog = false"
                :disabled="isSavingParam"
                outlined
              >
                Отмена
              </CustomButton>
              <CustomButton
                type="submit"
                :loading="isSavingParam"
                :disabled="!paramForm.bim_param_id"
              >
                {{ editingParam ? 'Сохранить' : 'Добавить' }}
              </CustomButton>
            </div>
          </q-form>
        </q-card-section>
      </q-card>
    </q-dialog>

    <!-- Прогресс-бар для группового удаления -->
    <q-dialog v-model="deletionProgressDialog" persistent>
      <q-card style="min-width: 400px">
        <q-card-section>
          <div class="text-h6">Удаление записей</div>
        </q-card-section>
        
        <q-card-section>
          <div style="margin-bottom: 10px;">
            Удалено: {{ deletionProgress.current }} из {{ deletionProgress.total }}
          </div>
          <q-linear-progress
            :value="deletionProgress.current / deletionProgress.total"
            color="primary"
            size="20px"
            class="q-mt-sm"
          >
            <div class="absolute-full flex flex-center">
              <q-badge color="white" text-color="primary" :label="`${Math.round((deletionProgress.current / deletionProgress.total) * 100)}%`" />
            </div>
          </q-linear-progress>
        </q-card-section>
      </q-card>
    </q-dialog>

        <!-- Диалог помощи -->
        <q-dialog v-model="helpDialog">
          <q-card style="max-width: 800px; width: 100%;">
            <q-card-section class="bim-modal__header">
              <div class="bim-modal__title">
                <q-icon name="help_outline" size="24px" style="margin-right: 8px;" />
                Руководство по работе с БИМ категориями
              </div>
              <q-btn icon="close" flat round dense v-close-popup class="bim-modal__close" />
            </q-card-section>

            <q-card-section class="help-content">
              <!-- Основы работы -->
              <div class="help-section">
                <h3 class="help-section__title">
                  <q-icon name="category" color="primary" />
                  Основы работы
                </h3>
                <p><strong>1. Выбор категории:</strong> Выберите категорию из выпадающего списка вверху страницы</p>
                <p><strong>2. Импорт данных:</strong> Кнопка "Импорт" позволяет загрузить данные из JSON файла</p>
              </div>

              <!-- Обычный режим -->
              <div class="help-section">
                <h3 class="help-section__title">
                  <q-icon name="construction" color="primary" />
                  Обычный режим работы
                </h3>
                
                <h4>Типы конструкций (левая колонка):</h4>
                <ul>
                  <li><strong>Поиск:</strong> Быстрый поиск по названию или ID типа</li>
                  <li><strong>Фильтр:</strong> Выпадающий список под поиском для фильтрации типов:
                    <ul style="margin-top: 4px;">
                      <li>"Все" - показывает все типы</li>
                      <li>"Только заполненные" - типы с заполненными "Тип линии" и "Замер"</li>
                      <li>"Только незаполненные" - типы с пропущенными полями (для проверки)</li>
                    </ul>
                  </li>
                  <li><strong>Выбор типа:</strong> Клик по типу отображает его параметры справа</li>
                  <li><strong>Чекбоксы:</strong> Выбор нескольких типов для группового удаления</li>
                  <li><strong>Кнопка "+":</strong> Добавление нового типа конструкции</li>
                  <li><strong>Кнопка "✏":</strong> Редактирование типа</li>
                  <li><strong>Кнопка "🗑":</strong> Групповое удаление выбранных типов</li>
                </ul>

                <h4>Параметры (правая колонка):</h4>
                <ul>
                  <li><strong>Поиск:</strong> Быстрый поиск по названию, ID или свойству параметра</li>
                  <li><strong>Фильтр (переключатель):</strong> "Показать все" / "Только активные" параметры</li>
                  <li><strong>Переключатель активности:</strong> Включение/выключение параметра для текущего типа</li>
                  <li><strong>Кнопка "+":</strong> Добавление существующего параметра к типу</li>
                  <li><strong>Кнопка "🗑":</strong> Групповое удаление выбранных связей с параметрами</li>
                </ul>
              </div>

              <!-- Групповой режим -->
              <div class="help-section">
                <h3 class="help-section__title">
                  <q-icon name="group_work" color="positive" />
                  Групповой режим
                </h3>
                <p><strong>Включение:</strong> Переключатель в заголовке "Типы конструкций"</p>
                <p><strong>Назначение:</strong> Массовое управление параметрами для нескольких типов одновременно</p>
                
                <h4>Как работать:</h4>
                <ol>
                  <li>Включите групповой режим переключателем</li>
                  <li>Выберите несколько типов конструкций (минимум 2) с помощью чекбоксов</li>
                  <li>Справа отобразятся общие параметры для всех выбранных типов</li>
                  <li>Используйте кнопки для массовых действий:</li>
                </ol>

                <h4>Доступные действия:</h4>
                <ul>
                  <li><strong>Кнопка "✓" (зеленая):</strong> Активировать выбранные параметры для всех типов</li>
                  <li><strong>Кнопка "✕" (оранжевая):</strong> Деактивировать выбранные параметры для всех типов</li>
                  <li><strong>Кнопка "🗑" (красная):</strong> Удалить выбранные параметры у всех типов</li>
                </ul>
              </div>

              <!-- Подсказки -->
              <div class="help-section">
                <h3 class="help-section__title">
                  <q-icon name="tips_and_updates" color="warning" />
                  Полезные советы
                </h3>
                <ul>
                  <li>🔍 Используйте поиск для быстрого нахождения нужных элементов</li>
                  <li>✅ Чекбокс "Выбрать все" выбирает все отфильтрованные элементы</li>
                  <li>💾 Фильтр "Только активные" сохраняется между сеансами работы</li>
                  <li>🔄 Данные автоматически обновляются после каждой операции</li>
                  <li>📊 Счетчики показывают количество отфильтрованных/всего элементов</li>
                  <li>⚡ Групповые операции отображают прогресс-бар</li>
                </ul>
              </div>
            </q-card-section>

            <q-card-actions align="right" style="padding: 12px 16px;">
              <CustomButton @click="helpDialog = false">
                Понятно
              </CustomButton>
            </q-card-actions>
          </q-card>
        </q-dialog>

        <!-- Диалоги подтверждения -->
        <q-dialog v-model="confirmDialog.show" persistent>
          <q-card style="min-width: 350px">
            <q-card-section>
              <div class="text-h6">{{ confirmDialog.title }}</div>
            </q-card-section>

            <q-card-section>
              {{ confirmDialog.message }}
            </q-card-section>

            <q-card-actions align="right">
              <q-btn flat label="Отмена" color="grey" @click="confirmDialog.show = false" />
              <q-btn unelevated label="Удалить" color="negative" @click="onConfirmDelete" />
            </q-card-actions>
          </q-card>
        </q-dialog>
      </div>
    </template>

<script>
import { useCustomSnackbar } from "@/_helpers/hooks/useCustomSnackbar";
import CustomInput from "@/components/form/CustomInput";
import CustomButton from "@/components/common/CustomButton.vue";
import CustomSelect from "@/components/form/CustomSelect";
import {
  getBimCategories,
  getBimTypes,
  getBimParams,
  getAllBimParams,
  getFloorParamTypes,
  importBimJson,
  saveBimType,
  deleteBimType,
  saveBimTypeParam,
  deleteBimTypeParam,
  getParamsByGroup,
  deleteParamFromGroup,
  setParamsSelectedGroup
} from "@/views/BimCategories/services";

export default {
  name: "BimCategories",
  components: {
    CustomInput,
    CustomButton,
    CustomSelect
  },
  data() {
    return {
      categories: [],
      types: [],
      params: [],
      allParams: [],
      floorParamTypes: [],
      selectedCategory: null,
      selectedCategoryId: null,
      selectedType: null,
      isLoadingCategories: false,
      isLoadingTypes: false,
      isLoadingParams: false,
      isLoadingAllParams: false,
      isLoadingFloorParamTypes: false,
      isImporting: false,
      isSaving: false,
      isSavingParam: false,
      typeDialog: false,
      paramDialog: false,
      editingType: null,
      editingParam: null,
      typeForm: {
        type_name: '',
        floor_param_type_id: null,
        floor_param_type_id2: null,
        floor_param_type_id3: null
      },
      paramForm: {
        bim_param_id: null
      },
      selectedTypes: [],
      selectedParams: [],
      isGroupMode: false,
      groupParams: [],
      isLoadingGroupParams: false,
      paramFilterMode: localStorage.getItem('bim_param_filter_mode') || 'all', // 'all' или 'active'
      groupParamSearchQuery: '',
      helpDialog: false,
      deletionProgressDialog: false,
      deletionProgress: {
        current: 0,
        total: 0
      },
      typeSearchQuery: '',
      paramSearchQuery: '',
      typeFilterMode: 'all',
      confirmDialog: {
        show: false,
        title: '',
        message: '',
        onConfirm: null
      },
    };
  },
  computed: {
    allTypesSelected() {
      return this.filteredTypes.length > 0 && this.selectedTypes.length === this.filteredTypes.length;
    },
        allParamsSelected() {
          return this.filteredParams.length > 0 && this.selectedParams.length === this.filteredParams.length;
        },
        allGroupParamsSelected() {
          return this.filteredGroupParams.length > 0 && this.selectedParams.length === this.filteredGroupParams.length;
        },
    filteredTypes() {
      let filtered = this.types;
      
      // Фильтр по заполненности
      if (this.typeFilterMode === 'filled') {
        filtered = filtered.filter(type => 
          type.floor_param_type_name && type.param_name
        );
      } else if (this.typeFilterMode === 'unfilled') {
        filtered = filtered.filter(type => 
          !type.floor_param_type_name || !type.param_name
        );
      }
      
      // Фильтр по поиску
      if (this.typeSearchQuery) {
        const query = this.typeSearchQuery.toLowerCase();
        filtered = filtered.filter(type => 
          type.type_name.toLowerCase().includes(query) ||
          type.bim_type_id.toString().includes(query)
        );
      }
      
      return filtered;
    },
    filteredParams() {
      let filtered = this.params;
      
      // Фильтр по активности
      if (this.paramFilterMode === 'active') {
        filtered = filtered.filter(param => param.is_selected);
      }
      
      // Фильтр по поиску
      if (this.paramSearchQuery) {
        const query = this.paramSearchQuery.toLowerCase();
        filtered = filtered.filter(param => 
          param.param_name.toLowerCase().includes(query) ||
          param.bim_param_id.toString().includes(query) ||
          (param.buffer_property_name && param.buffer_property_name.toLowerCase().includes(query))
        );
      }
      
      return filtered;
    },
    filteredGroupParams() {
      if (!this.groupParamSearchQuery) return this.groupParams;
      const query = this.groupParamSearchQuery.toLowerCase();
      return this.groupParams.filter(param => 
        param.param_name.toLowerCase().includes(query) ||
        param.bim_param_id.toString().includes(query) ||
        (param.buffer_property_name && param.buffer_property_name.toLowerCase().includes(query))
      );
    },
    formattedParamOptions() {
      return this.allParams.map(param => ({
        ...param,
        param_name: param.buffer_property_name 
          ? `${param.param_name} (${param.buffer_property_name})` 
          : param.param_name
      }));
    }
  },
  methods: {
    async loadCategories() {
      this.isLoadingCategories = true;
      const res = await getBimCategories();
      this.isLoadingCategories = false;
      if (!res) return;
      this.categories = res;
    },
    async loadFloorParamTypes() {
      this.isLoadingFloorParamTypes = true;
      const res = await getFloorParamTypes();
      this.isLoadingFloorParamTypes = false;
      if (!res) return;
      this.floorParamTypes = res;
    },
    onCategoryChange() {
      const category = this.categories.find(c => c.bim_category_id === this.selectedCategoryId);
      if (category) {
        this.selectCategory(category);
      }
    },
    async selectCategory(category) {
      this.selectedCategory = category;
      this.selectedType = null;
      this.params = [];
      this.selectedTypes = [];
      this.selectedParams = [];
      this.typeSearchQuery = '';
      this.paramSearchQuery = '';
      this.typeFilterMode = 'all';
      
      this.isLoadingTypes = true;
      const res = await getBimTypes(category.bim_category_id);
      this.isLoadingTypes = false;
      if (!res) return;
      this.types = res;
    },
    async selectType(type) {
      this.selectedType = type;
      this.selectedParams = [];
      this.paramSearchQuery = '';
      
      this.isLoadingParams = true;
      const res = await getBimParams(type.bim_type_id);
      this.isLoadingParams = false;
      if (!res) return;
      this.params = res;
    },
    async handleFileSelect(event) {
      const file = event.target.files[0];
      if (!file) return;

      this.isImporting = true;
      const res = await importBimJson(file);
      this.isImporting = false;

      if (res) {
        this.showSucceedSnackbar("JSON успешно импортирован");
        
        // Обновляем все данные
        await this.loadCategories();
        
        // Если была выбрана категория, обновляем типы
        if (this.selectedCategory) {
          const categoryId = this.selectedCategory.bim_category_id;
          this.selectedCategory = null;
          this.selectedType = null;
          this.types = [];
          this.params = [];
          
          // Находим обновленную категорию
          const updatedCategory = this.categories.find(
            c => c.bim_category_id === categoryId
          );
          if (updatedCategory) {
            await this.selectCategory(updatedCategory);
          }
        }
      }

      // Очищаем input для возможности загрузки того же файла снова
      event.target.value = "";
    },
    openTypeDialog(type = null) {
      this.editingType = type;
      if (type) {
        this.typeForm = {
          type_name: type.type_name,
          floor_param_type_id: type.floor_param_type_id || null,
          floor_param_type_id2: type.floor_param_type_id2 || null,
          floor_param_type_id3: type.floor_param_type_id3 || null
        };
      } else {
        this.typeForm = {
          type_name: '',
          floor_param_type_id: null,
          floor_param_type_id2: null,
          floor_param_type_id3: null
        };
      }
      this.typeDialog = true;
    },
    async saveType() {
      if (!this.typeForm.type_name) {
        return;
      }

      this.isSaving = true;
      const data = {
        bim_type_id: this.editingType?.bim_type_id || null,
        bim_category_id: this.selectedCategory.bim_category_id,
        type_name: this.typeForm.type_name,
        floor_param_type_id: this.typeForm.floor_param_type_id || null,
        floor_param_type_id2: this.typeForm.floor_param_type_id2 || null,
        floor_param_type_id3: this.typeForm.floor_param_type_id3 || null
      };

      const res = await saveBimType(data);
      this.isSaving = false;

      if (res) {
        this.showSucceedSnackbar(this.editingType ? 'Тип обновлен' : 'Тип добавлен');
        this.typeDialog = false;
        
        // Обновляем данные из БД без loader
        await this.loadTypesForCategorySilently(this.selectedCategory.bim_category_id);
      }
    },
    async loadTypesForCategory(categoryId) {
      this.isLoadingTypes = true;
      const res = await getBimTypes(categoryId);
      this.isLoadingTypes = false;
      if (!res) return;
      this.types = res;
    },
    async loadTypesForCategorySilently(categoryId) {
      const res = await getBimTypes(categoryId);
      if (!res) return;
      this.types = res;
    },
    toggleParamSelection(paramId, selected) {
      if (selected) {
        if (!this.selectedParams.includes(paramId)) {
          this.selectedParams.push(paramId);
        }
      } else {
        const index = this.selectedParams.indexOf(paramId);
        if (index > -1) {
          this.selectedParams.splice(index, 1);
        }
      }
    },
        toggleAllParams(selected) {
          if (selected) {
            // Добавляем все отфильтрованные параметры к уже выбранным
            const filteredIds = this.filteredParams.map(p => p.bim_param_id);
            this.selectedParams = [...new Set([...this.selectedParams, ...filteredIds])];
          } else {
            // Убираем все отфильтрованные параметры из выбранных
            const filteredIds = this.filteredParams.map(p => p.bim_param_id);
            this.selectedParams = this.selectedParams.filter(id => !filteredIds.includes(id));
          }
        },
        toggleAllGroupParams(selected) {
          if (selected) {
            // Добавляем все отфильтрованные групповые параметры к уже выбранным
            const filteredIds = this.filteredGroupParams.map(p => p.bim_param_id);
            this.selectedParams = [...new Set([...this.selectedParams, ...filteredIds])];
          } else {
            // Убираем все отфильтрованные групповые параметры из выбранных
            const filteredIds = this.filteredGroupParams.map(p => p.bim_param_id);
            this.selectedParams = this.selectedParams.filter(id => !filteredIds.includes(id));
          }
        },
    async toggleParamActive(param, isSelected) {
      const data = {
        bim_type_param_id: param.bim_type_param_id || null,
        bim_type_id: this.selectedType.bim_type_id,
        bim_param_id: param.bim_param_id,
        is_selected: isSelected
      };

      const res = await saveBimTypeParam(data);
      if (res) {
        this.showSucceedSnackbar(isSelected ? 'Параметр включен' : 'Параметр выключен');
        // Обновляем данные из БД без loader
        await this.reloadParamsSilently();
      }
    },
    async reloadParams() {
      if (!this.selectedType) return;
      this.isLoadingParams = true;
      const res = await getBimParams(this.selectedType.bim_type_id);
      this.isLoadingParams = false;
      if (!res) return;
      this.params = res;
    },
    async reloadParamsSilently() {
      if (!this.selectedType) return;
      const res = await getBimParams(this.selectedType.bim_type_id);
      if (!res) return;
      this.params = res;
    },
    async openParamDialog(param = null) {
      // Загружаем список всех параметров
      if (this.allParams.length === 0) {
        this.isLoadingAllParams = true;
        const res = await getAllBimParams();
        this.isLoadingAllParams = false;
        if (res) {
          this.allParams = res;
        }
      }

      this.editingParam = param;
      if (param) {
        this.paramForm = {
          bim_param_id: param.bim_param_id
        };
      } else {
        this.paramForm = {
          bim_param_id: null
        };
      }
      this.paramDialog = true;
    },
    async saveParam() {
      if (!this.paramForm.bim_param_id) {
        return;
      }

      this.isSavingParam = true;
      const data = {
        bim_type_param_id: this.editingParam?.bim_type_param_id || null,
        bim_type_id: this.selectedType.bim_type_id,
        bim_param_id: this.paramForm.bim_param_id,
        is_selected: true
      };

      const res = await saveBimTypeParam(data);
      this.isSavingParam = false;

      if (res) {
        this.showSucceedSnackbar(this.editingParam ? 'Параметр обновлен' : 'Параметр добавлен');
        this.paramDialog = false;
        
        // Обновляем данные из БД без loader
        await this.reloadParamsSilently();
      }
    },
    toggleTypeSelection(typeId, selected) {
      if (selected) {
        if (!this.selectedTypes.includes(typeId)) {
          this.selectedTypes.push(typeId);
        }
      } else {
        const index = this.selectedTypes.indexOf(typeId);
        if (index > -1) {
          this.selectedTypes.splice(index, 1);
        }
      }
      
      // В групповом режиме обновляем параметры при изменении выбранных типов
      if (this.isGroupMode && this.selectedTypes.length >= 2) {
        this.loadGroupParams();
      }
    },
    toggleAllTypes(selected) {
      if (selected) {
        // Добавляем все отфильтрованные типы к уже выбранным
        const filteredIds = this.filteredTypes.map(t => t.bim_type_id);
        this.selectedTypes = [...new Set([...this.selectedTypes, ...filteredIds])];
      } else {
        // Убираем все отфильтрованные типы из выбранных
        const filteredIds = this.filteredTypes.map(t => t.bim_type_id);
        this.selectedTypes = this.selectedTypes.filter(id => !filteredIds.includes(id));
      }
      
      // В групповом режиме обновляем параметры
      if (this.isGroupMode && this.selectedTypes.length >= 2) {
        this.loadGroupParams();
      }
    },
    confirmDeleteSelectedTypes() {
      this.confirmDialog = {
        show: true,
        title: 'Подтверждение',
        message: `Вы уверены, что хотите удалить ${this.selectedTypes.length} типов конструкций?`,
        onConfirm: async () => {
          await this.deleteSelectedTypes();
        }
      };
    },
    async deleteSelectedTypes() {
      this.deletionProgress = {
        current: 0,
        total: this.selectedTypes.length
      };
      this.deletionProgressDialog = true;

      const typesToDelete = [...this.selectedTypes];
      
      for (const typeId of typesToDelete) {
        await deleteBimType(typeId);
        this.deletionProgress.current++;
      }

      this.deletionProgressDialog = false;
      this.selectedTypes = [];
      this.showSucceedSnackbar(`Удалено ${typesToDelete.length} типов`);
      
      // Если удаляем выбранный тип, сбрасываем выбор
      if (this.selectedType && typesToDelete.includes(this.selectedType.bim_type_id)) {
        this.selectedType = null;
        this.params = [];
      }
      
      // Обновляем данные из БД без loader
      await this.loadTypesForCategorySilently(this.selectedCategory.bim_category_id);
    },
    confirmDeleteSelectedParams() {
      this.confirmDialog = {
        show: true,
        title: 'Подтверждение',
        message: `Вы уверены, что хотите удалить ${this.selectedParams.length} связей с параметрами?`,
        onConfirm: async () => {
          await this.deleteSelectedParams();
        }
      };
    },
    async deleteSelectedParams() {
      // Находим bim_type_param_id для выбранных параметров
      const typeParamIdsToDelete = this.params
        .filter(param => this.selectedParams.includes(param.bim_param_id))
        .map(param => param.bim_type_param_id)
        .filter(id => id); // убираем null/undefined
      
      if (typeParamIdsToDelete.length === 0) {
        this.showSucceedSnackbar('Нет связей для удаления');
        return;
      }

      this.deletionProgress = {
        current: 0,
        total: typeParamIdsToDelete.length
      };
      this.deletionProgressDialog = true;
      
      for (const typeParamId of typeParamIdsToDelete) {
        await deleteBimTypeParam(typeParamId);
        this.deletionProgress.current++;
      }

      this.deletionProgressDialog = false;
      this.selectedParams = [];
      this.showSucceedSnackbar(`Удалено ${typeParamIdsToDelete.length} связей`);
      
      // Обновляем данные из БД без loader
      await this.reloadParamsSilently();
    },
    async onConfirmDelete() {
      this.confirmDialog.show = false;
      if (this.confirmDialog.onConfirm) {
        await this.confirmDialog.onConfirm();
      }
    },
    // Методы для группового режима
        onParamFilterChange(mode) {
          this.paramFilterMode = mode;
          localStorage.setItem('bim_param_filter_mode', mode);
        },
        async onGroupModeChange(enabled) {
          this.selectedParams = [];
          
          if (enabled) {
            // Включен групповой режим
            this.selectedType = null;
            this.params = [];
            this.groupParamSearchQuery = '';
            
            if (this.selectedTypes.length >= 2) {
              await this.loadGroupParams();
            }
          } else {
            // Выключен групповой режим
            this.groupParams = [];
            this.groupParamSearchQuery = '';
          }
        },
    async loadGroupParams() {
      if (this.selectedTypes.length < 2) {
        this.groupParams = [];
        return;
      }
      
      this.isLoadingGroupParams = true;
      const res = await getParamsByGroup(this.selectedTypes);
      this.isLoadingGroupParams = false;
      
      if (res) {
        this.groupParams = res;
      } else {
        this.groupParams = [];
      }
    },
        async activateSelectedGroupParams(isActive) {
          if (this.selectedParams.length === 0 || this.selectedTypes.length < 2) {
            return;
          }
          
          const res = await setParamsSelectedGroup(this.selectedParams, this.selectedTypes, isActive);
          if (res) {
            const action = isActive ? 'Активировано' : 'Деактивировано';
            this.showSucceedSnackbar(`${action} ${this.selectedParams.length} параметров для ${this.selectedTypes.length} типов`);
            this.selectedParams = [];
            await this.loadGroupParams();
          }
        },
    confirmDeleteSelectedGroupParams() {
      this.confirmDialog = {
        show: true,
        title: 'Подтверждение',
        message: `Вы уверены, что хотите удалить ${this.selectedParams.length} параметров у ${this.selectedTypes.length} типов?`,
        onConfirm: async () => {
          await this.deleteSelectedGroupParams();
        }
      };
    },
    async deleteSelectedGroupParams() {
      if (this.selectedParams.length === 0 || this.selectedTypes.length < 2) {
        return;
      }
      
      this.deletionProgress = {
        current: 0,
        total: this.selectedParams.length
      };
      this.deletionProgressDialog = true;
      
      for (const paramId of this.selectedParams) {
        await deleteParamFromGroup(paramId, this.selectedTypes);
        this.deletionProgress.current++;
      }
      
      this.deletionProgressDialog = false;
      this.showSucceedSnackbar(`Удалено ${this.selectedParams.length} параметров у ${this.selectedTypes.length} типов`);
      this.selectedParams = [];
      
      // Перезагружаем общие параметры
      await this.loadGroupParams();
    },
  },
  mounted() {
    this.loadCategories();
    this.loadFloorParamTypes();
  },
  setup() {
    const { showSucceedSnackbar } = useCustomSnackbar();
    return { showSucceedSnackbar };
  },
};
</script>

<style scoped>
.bim-categories {
  padding: 8px;
  height: calc(100vh - 80px);
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.bim-categories__header {
  background: white;
  border: 1px solid #ddd;
  padding: 6px 8px;
  box-shadow: none;
}

.header-controls {
  display: flex;
  align-items: center;
  gap: 8px;
}

.header-controls :deep(.q-field--dense .q-field__control) {
  min-height: 32px;
  height: 32px;
}

.header-controls :deep(.q-field__label) {
  font-size: 11px;
}

.header-controls :deep(.q-field__native) {
  font-size: 11px;
  padding: 2px 8px;
}

.header-label {
  font-size: 12px;
  font-weight: 500;
  color: #333;
  white-space: nowrap;
}

.simple-select {
  min-width: 250px;
  padding: 4px 8px;
  font-size: 12px;
  border: 1px solid #ccc;
  border-radius: 3px;
  background: white;
  color: #333;
  outline: none;
  height: 28px;
}

.simple-select:hover {
  border-color: #1976d2;
}

.simple-select:focus {
  border-color: #1976d2;
  box-shadow: 0 0 0 2px rgba(25, 118, 210, 0.1);
}

.simple-select:disabled {
  background: #f5f5f5;
  color: #999;
  cursor: not-allowed;
}

.import-btn {
  width: auto;
  height: 32px;
  font-size: 11px;
}

.import-btn__icon {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background-color: #fff;
  color: #0060fe;
  padding: 1px;
}

.import-btn :deep(.q-btn__content) {
  display: flex;
  align-items: center;
  gap: 6px;
}

.bim-categories__content {
  display: flex;
  gap: 6px;
  flex: 1;
  overflow: hidden;
  min-height: 0;
}

.bim-categories__column {
  display: flex;
  flex-direction: column;
  background: white;
  border: 1px solid #ddd;
  overflow: hidden;
  flex: 1;
  min-width: 0;
}

.column-header {
  background: #f0f0f0;
  padding: 4px 8px;
  color: #333;
  font-weight: 600;
  font-size: 11px;
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  border-bottom: 1px solid #ddd;
}

.column-title {
  flex: 1;
}

.search-container {
  padding: 6px 8px;
  background: #ffffff;
  border-bottom: 1px solid #e0e0e0;
}

.search-input {
  font-size: 12px;
}

.search-input :deep(.q-field__control) {
  height: 28px;
  min-height: 28px;
}

.search-input :deep(.q-field__marginal) {
  height: 28px;
}

.search-input :deep(input) {
  font-size: 12px;
  padding: 2px 4px;
}

.filter-select {
  margin-top: 6px;
  width: 100%;
}

.column-list {
  overflow-y: auto;
  flex: 1;
  min-height: 0;
}

.list-item {
  border-bottom: 1px solid #f0f0f0;
  transition: background 0.1s;
  min-height: 32px;
  padding: 2px 4px !important;
}

.list-item :deep(.q-item__section) {
  padding: 0 4px;
}

.list-item:hover {
  background: #e8f4fd;
}

.list-item.q-item--active {
  background: #cce5ff;
  border-left: 2px solid #1976d2;
}

.item-label {
  font-weight: 500;
  color: #333;
  font-size: 11px;
  line-height: 1.3;
}

.item-caption {
  font-size: 10px;
  color: #999;
  margin-top: 1px;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #999;
  gap: 6px;
  padding: 16px;
}

.empty-state p {
  margin: 0;
  font-size: 11px;
}

.empty-state .q-icon {
  font-size: 28px !important;
}

.loading-container {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-height: 80px;
}

.params-table {
  flex: 1;
  overflow: auto;
  min-height: 0;
}

.params-table :deep(.q-table) {
  height: 100%;
}

.params-table :deep(.q-table__container) {
  max-height: 100%;
}

.params-table :deep(.q-table thead tr th) {
  position: sticky;
  top: 0;
  z-index: 1;
  background: #f5f5f5;
  font-weight: 600;
  color: #333;
  font-size: 10px;
  padding: 4px 6px;
  border-bottom: 1px solid #ddd;
}

.params-table :deep(.q-table tbody tr) {
  font-size: 11px;
  height: 28px;
}

.params-table :deep(.q-table tbody td) {
  padding: 2px 6px;
  border-bottom: 1px solid #f0f0f0;
}

.params-table :deep(.q-table tbody tr:hover) {
  background: #e8f4fd;
}

.params-table :deep(.q-table__card) {
  box-shadow: none;
}

.type-info-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 0 8px !important;
  min-width: 280px;
}

.type-line-block {
  background: #f8f9fa;
  border-radius: 4px;
  padding: 6px 8px;
  border-left: 3px solid #2196F3;
}

.type-line-block.line-empty {
  background: transparent;
  border-left-color: #e0e0e0;
  padding: 4px 8px;
}

.type-info-item {
  display: flex;
  gap: 4px;
  font-size: 10px;
  line-height: 1.3;
}

.type-info-item.line-header {
  font-size: 11px;
  font-weight: 600;
  margin-bottom: 4px;
}

.type-info-details {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-left: 8px;
  padding-left: 8px;
  border-left: 1px solid #e0e0e0;
}

.type-info-label {
  flex-shrink: 0;
  font-weight: 500;
}

.type-info-label-small {
  flex-shrink: 0;
  font-weight: 500;
  font-size: 9px;
  color: #666;
}

.type-info-value {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.type-info-value-small {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 9px;
  color: #444;
}

.action-buttons {
  width: auto !important;
  min-width: 60px;
}

.action-icons {
  display: flex;
  gap: 2px;
  opacity: 0;
  transition: opacity 0.15s;
}

.action-icons .q-btn {
  padding: 0;
  min-width: 20px;
  min-height: 20px;
}

.list-item:hover .action-icons {
  opacity: 1;
}

.list-item.q-item--active .action-icons {
  opacity: 1;
}

.param-actions .action-icons {
  display: flex;
  gap: 2px;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.15s;
}

.params-table :deep(tbody tr:hover .param-actions .action-icons) {
  opacity: 1;
}

.column-header .q-btn {
  padding: 2px;
  min-width: 24px;
  min-height: 24px;
}

.column-header .q-icon {
  font-size: 14px;
}

.column-header .q-badge {
  font-size: 10px;
  padding: 2px 4px;
}

.header-checkbox {
  margin-right: 4px;
}

.header-checkbox :deep(.q-checkbox__inner) {
  font-size: 16px;
  width: 16px;
  height: 16px;
}

.params-table :deep(.q-table__middle thead tr:first-child th:first-child) {
  width: 40px;
}

.params-table :deep(.q-checkbox__inner) {
  font-size: 16px;
  width: 16px;
  height: 16px;
}

/* Стили модального окна в стиле проекта */
.bim-modal {
  min-width: 500px;
  max-width: 700px;
}

.bim-modal__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #e5e7eb;
}

.bim-modal__title {
  font-size: 20px;
  font-weight: 600;
  color: #373D48;
  line-height: 20px;
}

.bim-modal__close {
  color: #9CA3AF;
}

.bim-modal__content {
  padding: 20px 24px;
}

.bim-form {
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 15px;
}

.bim-form__section {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.bim-form__item {
  width: 100%;
}

.bim-form__label {
  display: block;
  font-size: 12px;
  font-weight: 500;
  color: #333;
  margin-bottom: 6px;
}

.bim-modal__footer {
  width: 100%;
  height: 46px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;
  margin-top: 15px;
}

@media (max-width: 600px) {
  .bim-modal {
    min-width: 100%;
  }
}

/* Стили помощи */
.help-btn {
  margin-left: 8px;
}

.help-content {
  max-height: 70vh;
  overflow-y: auto;
  padding: 20px;
}

.help-section {
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e0e0e0;
}

.help-section:last-child {
  border-bottom: none;
}

.help-section__title {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.help-section h4 {
  font-size: 14px;
  font-weight: 600;
  color: #555;
  margin: 12px 0 8px 0;
}

.help-section p {
  font-size: 13px;
  color: #666;
  margin: 8px 0;
  line-height: 1.6;
}

.help-section ul,
.help-section ol {
  font-size: 13px;
  color: #666;
  margin: 8px 0;
  padding-left: 24px;
  line-height: 1.8;
}

.help-section li {
  margin: 6px 0;
}

.help-section strong {
  color: #333;
  font-weight: 600;
}
</style>

